import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Mail, Key, CheckCircle, XCircle, Send, Users } from "lucide-react";

const Settings = ({ onBack }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({
    resend_configured: false,
    notification_email: "",
    sender_email: "",
  });

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsResp, usersResp] = await Promise.all([
        axios.get(`${API}/settings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        axios.get(`${API}/admin/users`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }).catch(() => ({ data: [] })),
      ]);
      setSettings(settingsResp.data);
      setUsers(usersResp.data);
    } catch (err) {
      toast.error("Impossible de charger les parametres");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleTestEmail = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/settings/test-email`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success("Courriel de test envoye! Verifiez votre boite de reception.");
    } catch (error) {
      const message = error.response?.data?.detail || "Impossible d'envoyer le courriel de test";
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-white/50 hover:text-white hover:bg-white/5"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <div>
          <h2 className="font-heading text-2xl font-semibold text-white">Parametres</h2>
          <p className="text-white/50 text-sm">Configuration des notifications par courriel</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Resend API Key Status */}
        <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              settings.resend_configured ? 'bg-emerald-500/10' : 'bg-red-500/10'
            }`}>
              <Key className={`w-5 h-5 ${
                settings.resend_configured ? 'text-emerald-400' : 'text-red-400'
              }`} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-white">Cle API Resend</h3>
              <p className="text-sm text-white/50">Configuree via les variables d'environnement</p>
            </div>
          </div>
          
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg ${
            settings.resend_configured 
              ? 'bg-emerald-950/20 border border-emerald-900/30' 
              : 'bg-red-950/20 border border-red-900/30'
          }`}>
            {settings.resend_configured ? (
              <>
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-300 text-sm font-medium">
                  Cle API configuree et active
                </span>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-300 text-sm font-medium">
                  Cle API non configuree
                </span>
              </>
            )}
          </div>

          {!settings.resend_configured && (
            <div className="mt-4 bg-white/5 rounded-lg p-4 text-sm">
              <p className="text-white/70 font-medium mb-2">Comment configurer:</p>
              <ol className="list-decimal list-inside space-y-1 text-white/50">
                <li>Obtenez une cle API sur <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">resend.com</a></li>
                <li>Ajoutez-la dans la section <strong className="text-white/70">Secrets</strong> du deploiement</li>
                <li>Variable: <code className="bg-white/10 px-1.5 py-0.5 rounded text-white/80">RESEND_API_KEY</code></li>
              </ol>
            </div>
          )}
        </div>

        {/* Notification Email */}
        <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-white">Courriel de notification</h3>
              <p className="text-sm text-white/50">Les alertes de prix seront envoyees a votre courriel de connexion</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-white/5 border border-white/10">
            <Mail className="w-4 h-4 text-white/40" />
            <span className="text-white font-mono text-sm" data-testid="notification-email">
              {settings.notification_email || "Aucun courriel"}
            </span>
          </div>

          {settings.sender_email && (
            <p className="text-xs text-white/30 mt-3">
              Expediteur: {settings.sender_email}
            </p>
          )}
        </div>

        {/* Registered Users */}
        {users.length > 0 && (
          <div className="bg-[#121214] border border-white/10 rounded-xl p-6" data-testid="admin-users-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white">Usagers inscrits</h3>
                <p className="text-sm text-white/50">{users.length} compte{users.length !== 1 ? 's' : ''} enregistre{users.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left">
                    <th className="pb-3 text-white/50 font-medium">Courriel</th>
                    <th className="pb-3 text-white/50 font-medium">Inscription</th>
                    <th className="pb-3 text-white/50 font-medium text-center">Forfaits</th>
                    <th className="pb-3 text-white/50 font-medium text-center">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.email} className="border-b border-white/5 last:border-0">
                      <td className="py-3 text-white font-mono text-xs">
                        {u.email}
                        {u.is_admin && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-sans">Admin</span>
                        )}
                      </td>
                      <td className="py-3 text-white/50 text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-CA') : '-'}
                      </td>
                      <td className="py-3 text-white/70 text-center">{u.packages_count}</td>
                      <td className="py-3 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          u.auth_type === 'google' ? 'bg-blue-500/15 text-blue-300' : 'bg-white/10 text-white/50'
                        }`}>
                          {u.auth_type === 'google' ? 'Google' : 'Email'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Test Button */}
        <Button
          onClick={handleTestEmail}
          disabled={!settings.resend_configured || sending}
          className="bg-white text-black hover:bg-white/90 font-semibold"
          data-testid="test-email-button"
        >
          {sending ? (
            <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Envoyer un courriel test
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
